import ApisServiceApp from './ApisServiceApp'
import { normalizeDetailResponse, normalizeListResponse, type ListSummary } from '@/utils/apiResponseUtils'
import type {
  IYeuCauXuatHoaDon,
  IYeuCauXuatHoaDonPayload,
  IYeuCauXuatHoaDonBinhLuan,
  IYeuCauXuatHoaDonBinhLuanPayload,
  IYeuCauXuatHoaDonCapNhatPayload,
  IYeuCauXuatHoaDonFile,
  IYeuCauXuatHoaDonLichSu,
  IYeuCauXuatHoaDonXuatPayload,
  YcxLoaiFile,
} from '@/interfaces/yeuCauXuatHoaDonInterfaces'
import { mapYeuCauFromApi } from '@/components/admin/yeuCauXuatHoaDon/helpers/yeuCauXuatHoaDonHelpers'

// ⚠️ R-6: axios interceptor `ApisServiceApp.interceptors.response.use((response) => response.data)`
// đã unwrap 1 lần → consumer nhận payload. KHÔNG viết unwrap thừa (vd `.then(r => r.data.data)` — thừa 1 tầng → undefined).

const PREFIX = '/yeu-cau-xuat-hoa-don'

export const yeuCauXuatHoaDonApi = {
  // 1. Tạo YCX mới (NVBH)
  tao: async (payload: IYeuCauXuatHoaDonPayload): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.post(`${PREFIX}/tao-yeu-cau-xuat-hoa-don`, payload)
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 2. Danh sách YCX (queue KT + queue NVBH dùng cùng endpoint, khác query trangThai)
  danhSach: async (
    qs: string,
  ): Promise<{
    data: IYeuCauXuatHoaDon[]
    paging: { page: number; pageSize: number; total: number }
    summary?: ListSummary
  }> => {
    const res = await ApisServiceApp.get(`${PREFIX}/danh-sach-yeu-cau-xuat-hoa-don?${qs}`)
    const { data, paging, summary } = normalizeListResponse(
      res as Parameters<typeof normalizeListResponse>[0],
    )
    return {
      data: data.map((r) => mapYeuCauFromApi(r as Record<string, unknown>)),
      paging,
      summary,
    }
  },

  // 3. Chi tiết YCX (full-page detail)
  chiTiet: async (id: string): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-yeu-cau-xuat-hoa-don`)
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 4. Cập nhật YCX — mọi trạng thái (người mua, số HĐ, trạng thái, xóa file…)
  capNhat: async (id: string, payload: IYeuCauXuatHoaDonCapNhatPayload): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-yeu-cau-xuat-hoa-don`, payload)
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 5. [1]→[2] Gửi kế toán (NVBH)
  guiKT: async (id: string): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-gui-kt`, {})
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 6. [2]→[3] Gửi khách hàng (NVBH — D-03)
  guiKhach: async (id: string): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-gui-khach`, {})
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 7. [3]→[4] Khách đồng ý (NVBH — trust-but-verify)
  khachDongY: async (id: string): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-khach-dong-y`, {})
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 8. [3]→[2] Khách không đồng ý — reject loop (NVBH, D-04)
  khachKhongDongY: async (id: string, lyDoReject?: string): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.patch(
      `${PREFIX}/${id}/chuyen-trang-thai-khach-khong-dong-y`,
      { lyDoReject },
    )
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 9. [4]→[5] Xuất hóa đơn (KT — nhập số / ký hiệu / ngày từng hóa đơn đã phát hành)
  hoanThanh: async (id: string, payload: IYeuCauXuatHoaDonXuatPayload): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-hoan-thanh`, payload)
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 10. Hủy YCX — soft hủy (NVBH, [1][2][3][4], CẤM [5])
  huy: async (id: string, lyDoHuy: string): Promise<IYeuCauXuatHoaDon> => {
    const res = await ApisServiceApp.patch(`${PREFIX}/${id}/huy-yeu-cau-xuat-hoa-don`, { lyDoHuy })
    const raw = normalizeDetailResponse<Record<string, unknown>>(res)
    return mapYeuCauFromApi(raw)
  },

  // 11. Tạo bình luận (append-only thread — COORD-01)
  taoBinhLuan: async (
    id: string,
    payload: IYeuCauXuatHoaDonBinhLuanPayload,
  ): Promise<IYeuCauXuatHoaDonBinhLuan> => {
    const res = await ApisServiceApp.post(`${PREFIX}/${id}/binh-luan`, payload)
    return normalizeDetailResponse<IYeuCauXuatHoaDonBinhLuan>(res)
  },

  // 12. Danh sách bình luận
  danhSachBinhLuan: async (
    id: string,
    qs?: string,
  ): Promise<{
    data: IYeuCauXuatHoaDonBinhLuan[]
    paging: { page: number; pageSize: number; total: number }
  }> => {
    const query = qs ? `?${qs}` : ''
    const res = await ApisServiceApp.get(`${PREFIX}/${id}/danh-sach-binh-luan${query}`)
    return normalizeListResponse(
      res as Parameters<typeof normalizeListResponse>[0],
    ) as {
      data: IYeuCauXuatHoaDonBinhLuan[]
      paging: { page: number; pageSize: number; total: number }
    }
  },

  // 13. Tải lên file (multipart — interceptor đã unwrap, KHÔNG unwrap thêm)
  // NHAP: hóa đơn nháp | CHINH_THUC: hóa đơn đã phát hành — mọi trạng thái
  taiLenFile: async (id: string, file: File, loai: YcxLoaiFile = 'NHAP'): Promise<IYeuCauXuatHoaDonFile> => {
    const formData = new FormData()
    formData.append('loai', loai)
    formData.append('file', file)
    const res = await ApisServiceApp.post(`${PREFIX}/${id}/tai-len-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return normalizeDetailResponse<IYeuCauXuatHoaDonFile>(res)
  },

  // 14. Danh sách file nháp (multi-version)
  danhSachFile: async (
    id: string,
  ): Promise<{
    data: IYeuCauXuatHoaDonFile[]
    paging: { page: number; pageSize: number; total: number }
  }> => {
    const res = await ApisServiceApp.get(`${PREFIX}/${id}/danh-sach-file`)
    return normalizeListResponse(
      res as Parameters<typeof normalizeListResponse>[0],
    ) as {
      data: IYeuCauXuatHoaDonFile[]
      paging: { page: number; pageSize: number; total: number }
    }
  },

  // 15. Lịch sử chuyển trạng thái (audit timeline — EVID-01)
  lichSuChuyenTrangThai: async (
    id: string,
    qs?: string,
  ): Promise<{
    data: IYeuCauXuatHoaDonLichSu[]
    paging: { page: number; pageSize: number; total: number }
  }> => {
    const query = qs ? `?${qs}` : ''
    const res = await ApisServiceApp.get(`${PREFIX}/${id}/lich-su-chuyen-trang-thai${query}`)
    return normalizeListResponse(
      res as Parameters<typeof normalizeListResponse>[0],
    ) as {
      data: IYeuCauXuatHoaDonLichSu[]
      paging: { page: number; pageSize: number; total: number }
    }
  },
}
